// components/Header.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { trackActivity } from "../utils/activityTracker";
import { getAlerts } from "../controllers/alertController";
import AlertBox from "./AlertBox";
import { useBranch } from "../context/BranchContext";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBranch, setSelectedBranch, branchList, refreshBranches } = useBranch();
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  let user = null;
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      user = JSON.parse(storedUser);
    }
  } catch (error) {
    user = null;
  }

  const storedUsername =
    localStorage.getItem("username") ||
    user?.username ||
    user?.userName ||
    user?.email ||
    user?.emailAddress;

  const [unreadCount, setUnreadCount] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [energyDropdownOpen, setEnergyDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  // Active Live sub-tab, kept in sync with LiveTab (sessionStorage + custom event).
  const [activeLiveTab, setActiveLiveTab] = useState(
    () => sessionStorage.getItem("enviz-pending-tab") || "diagram",
  );

  const closeAllDropdowns = () => {
    setEnergyDropdownOpen(false);
    setProfileDropdownOpen(false);
    setBranchDropdownOpen(false);
  };

  // LiveTab can switch its own tab; mirror it so the tab bar highlight stays correct.
  useEffect(() => {
    const onTabChange = (e) => {
      if (e?.detail) setActiveLiveTab(e.detail);
    };
    window.addEventListener("enviz-tab-change", onTabChange);
    return () => window.removeEventListener("enviz-tab-change", onTabChange);
  }, []);

  useEffect(() => {
    if (storedUsername && refreshBranches) {
      refreshBranches();
    }
  }, [storedUsername, refreshBranches]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!storedUsername) return;
      try {
        const data = await getAlerts(storedUsername);
        setUnreadCount(data.unread || 0);
      } catch (e) { }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [storedUsername]);

  const remoteLogo = storedUsername && !logoError ? `/api/get_logo?username=${storedUsername}` : null;
  const companyLogo = remoteLogo || user?.logo || user?.logoUrl || user?.logoURL || user?.companyLogo || user?.clientLogo || user?.brandingLogo;
  const profileImage = user?.profilePicture || user?.profile_image || user?.profileImage || user?.profile_pic || user?.avatar || user?.avatarUrl || user?.avatarURL || user?.photo || user?.photoUrl || user?.photoURL || user?.image || user?.imageUrl || user?.imageURL;
  const avatarImage = companyLogo || profileImage;
  const userInitial = user?.displayName?.charAt(0)?.toUpperCase();

  const handleLogout = () => {
    trackActivity({ action: "LOGOUT", tab_name: "Header" });
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const showBillReconciliation = storedUsername === 'jawa';

  const isEfficiencyActive = location.pathname.startsWith("/tools/efficiency-initiatives");
  const isEnergyActive =
    location.pathname.startsWith("/live") ||
    location.pathname.startsWith("/historical") ||
    location.pathname.startsWith("/ensight") ||
    location.pathname.startsWith("/tools/off-grid-calculator") ||
    location.pathname.startsWith("/bill-reconciliation") ||
    location.pathname.startsWith("/customization");
  const isTemperatureActive = location.pathname.startsWith("/tools/environment");

  const liveSubItems = [
    { key: "diagram", label: "Diagram View" },
    { key: "table", label: "Table View" },
    { key: "distribution", label: "Distribution View" },
    { key: "forecast", label: "Forecasting" },
    { key: "customization", label: "Customizations", path: "/customization" },
  ];

  const historicalSubItems = [
    { path: "/historical", label: "Overview" },
    { path: "/ensight", label: "Insights" },
  ];

  const temperatureSubItems = [
    { path: "/tools/environment", label: "Live" },
    { path: "/tools/environment/historical", label: "Historical" },
  ];

  const goToLiveTab = (item) => {
    if (item.path) {
      navigate(item.path);
    } else {
      sessionStorage.setItem("enviz-pending-tab", item.key);
      window.dispatchEvent(new CustomEvent("enviz-tab-change", { detail: item.key }));
      setActiveLiveTab(item.key);
      navigate("/live");
    }
    closeAllDropdowns();
  };

  // Sub-navigation shown as a tab bar under the header, contextual to the section.
  const isLiveSection =
    location.pathname.startsWith("/live") || location.pathname.startsWith("/customization");
  const isHistoricalSection =
    location.pathname.startsWith("/historical") || location.pathname.startsWith("/ensight");
  const isTemperatureSection = location.pathname.startsWith("/tools/environment");

  const subTabs = isLiveSection
    ? liveSubItems.map((item) => ({
        key: item.key,
        label: item.label,
        active: item.path
          ? location.pathname.startsWith(item.path)
          : location.pathname.startsWith("/live") && activeLiveTab === item.key,
        onClick: () => goToLiveTab(item),
      }))
    : isHistoricalSection
      ? historicalSubItems.map((item) => ({
          key: item.path,
          label: item.label,
          active:
            item.path === "/historical"
              ? location.pathname === "/historical"
              : location.pathname.startsWith(item.path),
          onClick: () => {
            navigate(item.path);
            closeAllDropdowns();
          },
        }))
      : isTemperatureSection
        ? temperatureSubItems.map((item) => ({
            key: item.path,
            label: item.label,
            // "/tools/environment" is Live, so match it exactly (Historical is a sub-path).
            active:
              item.path === "/tools/environment"
                ? location.pathname === "/tools/environment"
                : location.pathname.startsWith(item.path),
            onClick: () => {
              navigate(item.path);
              closeAllDropdowns();
            },
          }))
        : [];

  return (
    <>
    <div style={styles.header}>
      <div style={styles.brandArea}>
        <a href="https://enfo.ai/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          <div style={styles.logoWrapper}>
            <h2 style={styles.logo}>
              <span style={styles.logoPrimary}>En</span>
              <span style={styles.logoSecondary}>Viz</span>
            </h2>
          </div>
        </a>
        <nav style={styles.nav}>

          {/* Efficiency Tab */}
          <NavLink
            to="/tools/efficiency-initiatives"
            onClick={closeAllDropdowns}
            style={() => ({
              ...styles.link,
              backgroundColor: isEfficiencyActive ? "#fff" : "transparent",
              color: isEfficiencyActive ? "#1BA08E" : "#fff",
              fontWeight: isEfficiencyActive ? 700 : 600,
              boxShadow: isEfficiencyActive ? "0 2px 8px rgba(27,160,142,0.10)" : undefined,
              border: isEfficiencyActive ? "1.5px solid #1BA08E" : "1.5px solid transparent",
              transition: "all 0.2s",
            })}
          >
            Efficiency
          </NavLink>

          {/* Energy Tab with hover dropdown */}
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setEnergyDropdownOpen(true)}
            onMouseLeave={() => setEnergyDropdownOpen(false)}
          >
            <div style={{
              ...styles.link,
              backgroundColor: isEnergyActive ? "#fff" : "transparent",
              color: isEnergyActive ? "#1BA08E" : "#fff",
              fontWeight: isEnergyActive ? 700 : 600,
              boxShadow: isEnergyActive ? "0 2px 8px rgba(27,160,142,0.10)" : undefined,
              border: isEnergyActive ? "1.5px solid #1BA08E" : "1.5px solid transparent",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
            }}>
              Energy <span style={{ fontSize: 8, opacity: 0.6 }}>&#9660;</span>
            </div>
            {energyDropdownOpen && (
              <div style={{
                position: "absolute", top: "100%", left: 0, marginTop: 0,
                background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0",
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 180, overflow: "visible",
              }}>
                {/* Live -> opens Diagram View by default; sub-views live in the tab bar below */}
                <div
                  onClick={() => goToLiveTab(liveSubItems[0])}
                  style={{
                    padding: "10px 16px", fontSize: 13, fontWeight: 500,
                    color: location.pathname.startsWith("/live") ? "#1BA08E" : "#374151",
                    cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = location.pathname.startsWith("/live") ? "#1BA08E" : "#374151"; }}
                >
                  Live
                </div>

                {/* Historical -> opens Overview by default; sub-views in the tab bar below */}
                <div
                  onClick={() => { navigate("/historical"); closeAllDropdowns(); }}
                  style={{
                    padding: "10px 16px", fontSize: 13, fontWeight: 500,
                    color: (location.pathname.startsWith("/historical") || location.pathname.startsWith("/ensight")) ? "#1BA08E" : "#374151",
                    cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = (location.pathname.startsWith("/historical") || location.pathname.startsWith("/ensight")) ? "#1BA08E" : "#374151"; }}
                >
                  Historical
                </div>

                {/* Off Grid Calculator */}
                <div
                  onClick={() => { navigate("/tools/off-grid-calculator"); closeAllDropdowns(); }}
                  style={{
                    padding: "10px 16px",
                    fontSize: 13,
                    fontWeight: 500,
                    color: location.pathname.startsWith("/tools/off-grid-calculator") ? "#1BA08E" : "#374151",
                    cursor: "pointer",
                    borderBottom: showBillReconciliation ? "1px solid #f1f5f9" : "none",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#374151"; }}
                >
                  Off Grid Calculator
                </div>

                {/* Bill Reconciliation (only for jawa) */}
                {showBillReconciliation && (
                  <div
                    onClick={() => { navigate("/bill-reconciliation"); closeAllDropdowns(); }}
                    style={{
                      padding: "10px 16px",
                      fontSize: 13,
                      fontWeight: 500,
                      color: location.pathname.startsWith("/bill-reconciliation") ? "#1BA08E" : "#374151",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#374151"; }}
                  >
                    Bill Reconciliation
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Temperature Tab */}
          <NavLink
            to="/tools/environment"
            onClick={closeAllDropdowns}
            style={() => ({
              ...styles.link,
              backgroundColor: isTemperatureActive ? "#fff" : "transparent",
              color: isTemperatureActive ? "#1BA08E" : "#fff",
              fontWeight: isTemperatureActive ? 700 : 600,
              boxShadow: isTemperatureActive ? "0 2px 8px rgba(27,160,142,0.10)" : undefined,
              border: isTemperatureActive ? "1.5px solid #1BA08E" : "1.5px solid transparent",
              transition: "all 0.2s",
            })}
          >
            Temperature
          </NavLink>

          {/* Flow Tab - Coming Soon */}
          <div
            style={{ ...styles.link, border: "1.5px solid transparent", opacity: 0.55, cursor: "not-allowed", userSelect: "none", position: "relative" }}
            title="Coming Soon"
          >
            Flow
            <span style={{ position: "absolute", top: -6, right: -4, background: "#f59e0b", color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 4, letterSpacing: "0.03em" }}>
              SOON
            </span>
          </div>

          {/* Branches dropdown */}
          {branchList.length > 0 && (
            <div style={{ position: "relative" }}>
              <button
                onClick={() => { setEnergyDropdownOpen(false); setProfileDropdownOpen(false); setBranchDropdownOpen((v) => !v); }}
                style={{ ...styles.link, cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", display: "flex", alignItems: "center", gap: 4, outline: "none", background: "transparent", border: "1.5px solid transparent", transition: "all 0.2s" }}
              >
                Branches <span style={{ fontSize: 8, opacity: 0.6 }}>&#9660;</span>
              </button>
              {branchDropdownOpen && (
                <>
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} onClick={closeAllDropdowns}></div>
                  <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 180, overflow: "hidden" }}>
                    <div
                      onClick={() => { setSelectedBranch("All"); closeAllDropdowns(); }}
                      style={{ padding: "10px 16px", fontSize: 13, fontWeight: selectedBranch === "All" ? 700 : 500, color: selectedBranch === "All" ? "#1BA08E" : "#374151", cursor: "pointer", borderBottom: branchList.length > 0 ? "1px solid #f1f5f9" : "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                      onMouseEnter={(e) => { e.target.style.background = "#f0fdf4"; e.target.style.color = "#1BA08E"; }}
                      onMouseLeave={(e) => { e.target.style.background = "#fff"; e.target.style.color = selectedBranch === "All" ? "#1BA08E" : "#374151"; }}
                    >
                      All Branches {selectedBranch === "All" && <span>&#10003;</span>}
                    </div>
                    {branchList.map((branch, idx) => (
                      <div key={branch.name}
                        onClick={() => { setSelectedBranch(branch.name); closeAllDropdowns(); }}
                        style={{ padding: "10px 16px", fontSize: 13, fontWeight: selectedBranch === branch.name ? 700 : 500, color: selectedBranch === branch.name ? "#1BA08E" : "#374151", cursor: "pointer", borderBottom: idx !== branchList.length - 1 ? "1px solid #f1f5f9" : "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        onMouseEnter={(e) => { e.target.style.background = "#f0fdf4"; e.target.style.color = "#1BA08E"; }}
                        onMouseLeave={(e) => { e.target.style.background = "#fff"; e.target.style.color = selectedBranch === branch.name ? "#1BA08E" : "#374151"; }}
                      >
                        {branch.name} {selectedBranch === branch.name && <span>&#10003;</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </nav>
      </div>

      <div style={styles.right}>
        {user && (
          <>
            <div style={{ position: "relative", marginRight: "4px" }}>
              <button onClick={() => setShowAlerts(!showAlerts)} style={styles.bellButton} title="Notifications">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                {unreadCount > 0 && (
                  <span style={styles.notificationBadge}>{unreadCount > 9 ? "9+" : unreadCount}</span>
                )}
              </button>
              <AlertBox
                isOpen={showAlerts}
                onClose={() => {
                  setShowAlerts(false);
                  if (storedUsername) {
                    getAlerts(storedUsername).then(data => setUnreadCount(data.unread || 0)).catch(() => { });
                  }
                }}
              />
            </div>

            {/* Client Name / Profile Dropdown */}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => { setEnergyDropdownOpen(false); setBranchDropdownOpen(false); setProfileDropdownOpen((v) => !v); }}
                style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 8, transition: "background 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <span style={styles.clientName}>{user?.client_name || user?.username}</span>
                {avatarImage ? (
                  <img src={avatarImage} alt={user?.displayName || "Profile"} style={styles.avatar} onError={() => setLogoError(true)} />
                ) : (
                  userInitial && <div style={styles.avatarFallback}>{userInitial}</div>
                )}
                <span style={{ fontSize: 8, opacity: 0.6, color: "#fff" }}>&#9660;</span>
              </button>

              {profileDropdownOpen && (
                <>
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} onClick={() => setProfileDropdownOpen(false)}></div>
                  <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.14)", zIndex: 100, minWidth: 190, overflow: "hidden" }}>
                    <div
                      onClick={() => { navigate("/info"); setProfileDropdownOpen(false); }}
                      style={{ padding: "11px 16px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#374151"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                      Settings
                    </div>
                    <div
                      onClick={() => { navigate("/tools/complaint-ticket"); setProfileDropdownOpen(false); }}
                      style={{ padding: "11px 16px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.color = "#1BA08E"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#374151"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      Complaint Ticket
                    </div>
                    <div
                      onClick={() => { setProfileDropdownOpen(false); handleLogout(); }}
                      style={{ padding: "11px 16px", fontSize: 13, fontWeight: 500, color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#fef2f2"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      Logout
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>

    {/* Contextual sub-navigation tabs (Live views / Historical views) */}
    {subTabs.length > 0 && (
      <div style={styles.subTabBar}>
        {subTabs.map((tab) => (
          <div
            key={tab.key}
            onClick={tab.onClick}
            style={{
              ...styles.subTab,
              ...(tab.active ? styles.subTabActive : {}),
            }}
            onMouseEnter={(e) => { if (!tab.active) e.currentTarget.style.color = "#1BA08E"; }}
            onMouseLeave={(e) => { if (!tab.active) e.currentTarget.style.color = "#64748b"; }}
          >
            {tab.label}
          </div>
        ))}
      </div>
    )}
    </>
  );
};

const styles = {
  subTabBar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "0 24px",
    background: "#fff",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  subTab: {
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 600,
    color: "#64748b",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "color 0.15s, border-color 0.15s",
    whiteSpace: "nowrap",
  },
  subTabActive: {
    color: "#1BA08E",
    borderBottom: "2px solid #1BA08E",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    backgroundColor: "#1BA08E",
    color: "white",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    gap: "20px",
    flexWrap: "wrap",
  },
  brandArea: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap",
  },
  logoWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#fff",
    padding: "6px 18px 6px 12px",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(27,160,142,0.07)",
  },
  logo: {
    margin: 0,
    fontSize: "24px",
    letterSpacing: "1px",
    display: "flex",
    alignItems: "baseline",
    gap: "2px",
  },
  logoPrimary: { color: "#1BA08E" },
  logoSecondary: { color: "#3D5DAA" },
  nav: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  link: {
    padding: "8px 14px",
    borderRadius: "6px",
    color: "#fff",
    transition: "all 0.2s",
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  avatar: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    objectFit: "contain",
    backgroundColor: "#fff",
    padding: "4px",
  },
  avatarFallback: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    backgroundColor: "#1BA08E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    color: "#fff",
  },
  clientName: {
    color: "#fff",
    fontWeight: "600",
    fontSize: "16px",
  },
  bellButton: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#fff",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    transition: "background-color 0.2s",
    position: "relative",
  },
  notificationBadge: {
    position: "absolute",
    top: "2px",
    right: "2px",
    minWidth: "16px",
    height: "16px",
    backgroundColor: "#ff4d4f",
    borderRadius: "8px",
    border: "1.5px solid #1BA08E",
    color: "#fff",
    fontSize: "10px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 3px",
  },
};

export default Header;
